import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import mhApi from '../services/marketing-hub';

/* Helpers */
const HEALTH = {
  critical: { label: '🔴 Critique', cls: 'status-error' },
  warning:  { label: '🟠 Vigilance', cls: 'status-warning' },
  ok:       { label: '🟢 OK', cls: 'status-success' },
  inactive: { label: '⚪ Inactif', cls: 'status-info' },
};
// Libellés + couleur de chaque anomalie possible (alignés sur le backend make-sync.js).
const ANOMALY = {
  invalid:         { label: 'Invalide', cls: 'status-error' },
  last_run_failed: { label: 'Dernière exéc en échec', cls: 'status-error' },
  errors:          { label: 'Erreurs non résolues', cls: 'status-error' },
  never_run:       { label: 'Jamais exécuté', cls: 'status-warning' },
  dlq:             { label: 'Incomplètes (DLQ)', cls: 'status-warning' },
};
function fmtDate(d) { return d ? new Date(d).toLocaleDateString('fr-FR') : '—'; }
function fmtDateTime(d) { return d ? new Date(d).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—'; }
function fmtNum(n) { return Number(n || 0).toLocaleString('fr-FR'); }
function fmtBytes(b) {
  b = Number(b || 0);
  if (b > 1e9) return `${(b / 1e9).toFixed(1)} Go`;
  if (b > 1e6) return `${(b / 1e6).toFixed(1)} Mo`;
  if (b > 1e3) return `${(b / 1e3).toFixed(0)} Ko`;
  return `${b} o`;
}
function cadence(s) {
  if (s.scheduling_type === 'immediately') return 'Webhook (instantané)';
  if (s.scheduling_type === 'indefinitely') {
    const m = Math.round((s.scheduling_interval || 0) / 60);
    return m >= 60 ? `Toutes les ${Math.round(m / 60)} h` : `Toutes les ${m || '?'} min`;
  }
  if (s.scheduling_type === 'on-demand') return 'À la demande';
  if (s.scheduling_type === 'weekly') return 'Hebdomadaire';
  if (s.scheduling_type === 'daily') return 'Quotidien';
  if (s.scheduling_type === 'monthly') return 'Mensuel';
  return s.scheduling_type || '—';
}
function pctBand(pct) {
  if (pct == null) return 'var(--color-accent)';
  if (pct >= 90) return 'var(--color-error)';
  if (pct >= 75) return 'var(--color-warning)';
  return 'var(--color-accent)';
}

export default function MhMake() {
  const [config, setConfig] = useState(null);
  const [overview, setOverview] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [trend, setTrend] = useState([]);
  const [filters, setFilters] = useState({ team: '', status: '', search: '' });
  const [selected, setSelected] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [scope, setScope] = useState('active'); // 'active' (défaut, masque les inactifs) | 'inactive'

  const load = useCallback(async () => {
    try {
      const [c, o, s, t] = await Promise.all([
        mhApi.makeConfig(), mhApi.makeOverview(), mhApi.makeScenarios(), mhApi.makeUsageTrend(),
      ]);
      setConfig(c.data); setOverview(o.data); setScenarios(s.data); setTrend(t.data);
    } catch { /* */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true);
    try { await mhApi.makeSync(); await load(); }
    catch (e) { alert(e.response?.data?.error || 'Erreur de synchronisation'); }
    finally { setSyncing(false); }
  };

  const teams = useMemo(() => [...new Map(scenarios.map(s => [s.team_id, s.team_name])).entries()], [scenarios]);
  const activeCount = useMemo(() => scenarios.filter(s => s.health !== 'inactive').length, [scenarios]);
  const inactiveCount = useMemo(() => scenarios.filter(s => s.health === 'inactive').length, [scenarios]);
  const anomalyCount = useMemo(() => scenarios.filter(s => (s.anomalies || []).length > 0).length, [scenarios]);
  const filtered = scenarios.filter(s => {
    let inScope;
    if (scope === 'inactive') inScope = s.health === 'inactive';
    else if (scope === 'anomalies') inScope = (s.anomalies || []).length > 0;
    else inScope = s.health !== 'inactive';
    return inScope &&
      (!filters.team || String(s.team_id) === filters.team) &&
      (scope !== 'active' || !filters.status || s.health === filters.status) &&
      (!filters.search || `${s.name} ${s.team_name}`.toLowerCase().includes(filters.search.toLowerCase()));
  });

  if (config && !config.configured && !(overview && overview.total > 0)) {
    return (
      <div className="mh-make-unconfigured">
        <h3>⚙️ Make non configuré</h3>
        <p>Pour activer le monitoring, ajoute un token API Make au backend :</p>
        <ol>
          <li>Dans Make → <strong>Profil → API</strong>, génère un token (scopes lecture : <code>scenarios:read</code>, <code>organizations:read</code>, <code>teams:read</code>, <code>dlqs:read</code>).</li>
          <li>Renseigne <code>MAKE_API_TOKEN</code> dans le <code>.env</code> du backend (zone <code>{config.zone}</code>, org <code>{config.org_id}</code>).</li>
          <li>Redémarre le backend : la synchronisation démarre automatiquement.</li>
        </ol>
      </div>
    );
  }

  const u = overview?.usage;
  const opsPct = u && u.operations_limit ? Math.round((Number(u.operations) / Number(u.operations_limit)) * 100) : null;
  const trPct = u && u.transfer_limit ? Math.round((Number(u.transfer) / Number(u.transfer_limit)) * 100) : null;
  const c = overview?.counts || {};
  const burn = overview?.burn;

  return (
    <div className="mh-make">
      {config && !config.configured && (
        <div className="mh-make-notice"><span className="mh-make-warn">⚠️ Token Make absent : données issues du dernier seed, non rafraîchies en direct. Renseigne <code>MAKE_API_TOKEN</code> pour la synchro automatique.</span></div>
      )}
      {/* Bandeau organisation */}
      <div className="mh-make-org">
        <div className="mh-make-gauge-wrap">
          <div className="mh-make-gauge-head">
            <span>Opérations consommées (cycle Make)</span>
            <strong>{fmtNum(u?.operations)} / {fmtNum(u?.operations_limit)}{opsPct != null ? ` · ${opsPct}%` : ''}</strong>
          </div>
          <div className="mh-make-bar"><div style={{ width: `${Math.min(100, opsPct || 0)}%`, background: pctBand(opsPct) }} /></div>
          <div className="mh-make-gauge-foot">
            <span>Cycle {fmtDate(u?.cycle_start)} → {fmtDate(u?.cycle_end)}{burn ? ` · ${burn.days_remaining} j restants` : ''}</span>
            {burn && (
              <span className={burn.projected_pct >= 100 ? 'mh-make-danger' : burn.projected_pct >= 80 ? 'mh-make-warn' : 'mh-muted'}>
                Projection fin de cycle : {fmtNum(burn.projected)} ({burn.projected_pct}%)
              </span>
            )}
            {u?.auto_purchasing && <span className="mh-make-warn">⚠️ Auto-achat activé (dépassements facturés)</span>}
          </div>
          {trend.length > 1 && (
            <ResponsiveContainer width="100%" height={60}>
              <LineChart data={trend.map(p => ({ d: fmtDate(p.captured_at), op: Number(p.operations) }))}>
                <XAxis dataKey="d" hide /><YAxis hide domain={[0, Number(u?.operations_limit) || 'auto']} />
                <Tooltip formatter={(v) => fmtNum(v)} />
                <Line type="monotone" dataKey="op" stroke="var(--color-accent)" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="mh-make-stats">
          <div className="mh-make-stat"><span>Transfert</span><strong>{fmtBytes(u?.transfer)}{trPct != null ? ` (${trPct}%)` : ''}</strong></div>
          <div className="mh-make-stat"><span>Scénarios actifs</span><strong>{u?.active_scenarios ?? '—'}</strong></div>
          <div className="mh-make-stat mh-make-stat-crit"><span>Critiques</span><strong>{c.critical || 0}</strong></div>
          <div className="mh-make-stat mh-make-stat-dlq" title="Scénarios avec des exécutions incomplètes (DLQ) à rejouer"><span>Incomplètes (DLQ)</span><strong>{overview?.dlq_scenarios || 0}</strong></div>
        </div>
      </div>

      {/* Vue Anomalies / Actifs / Inactifs */}
      <div className="mh-scope-tabs">
        <button className={`${scope === 'anomalies' ? 'active' : ''} ${anomalyCount > 0 ? 'mh-scope-alert' : ''}`} onClick={() => { setScope('anomalies'); setFilters(f => ({ ...f, status: '' })); }}>
          ⚠️ Anomalies ({anomalyCount})
        </button>
        <button className={scope === 'active' ? 'active' : ''} onClick={() => { setScope('active'); setFilters(f => ({ ...f, status: '' })); }}>
          Actifs ({activeCount})
        </button>
        <button className={scope === 'inactive' ? 'active' : ''} onClick={() => { setScope('inactive'); setFilters(f => ({ ...f, status: '' })); }}>
          ⚪ Inactifs ({inactiveCount})
        </button>
      </div>

      {/* Filtres */}
      <div className="page-toolbar">
        <input className="page-search" placeholder="🔍 Rechercher..." value={filters.search} onChange={e => setFilters({ ...filters, search: e.target.value })} />
        <select className="page-select" value={filters.team} onChange={e => setFilters({ ...filters, team: e.target.value })}>
          <option value="">Toutes les teams</option>
          {teams.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
        {scope === 'active' && (
          <select className="page-select" value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })}>
            <option value="">Tous les statuts</option>
            <option value="critical">Critique</option><option value="warning">Vigilance</option>
            <option value="ok">OK</option>
          </select>
        )}
        <span className="mh-count">{filtered.length} scénario(s)</span>
        <button className="page-header-btn" disabled={syncing} onClick={sync} style={{ marginLeft: 8 }}>{syncing ? '⏳ Sync...' : '🔄 Synchroniser'}</button>
      </div>

      {/* Tableau */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Statut</th><th>Scénario</th><th>Team</th><th>Cadence</th>
              <th title="Date et résultat de la dernière exécution réelle du scénario">Dernière exéc</th>
              <th title="Toutes les anomalies détectées sur le scénario">Anomalies</th>
              <th>Dernière modif</th><th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(s => {
              const anomalies = s.anomalies || [];
              return (
              <tr key={s.scenario_id} className="mh-row" onClick={() => setSelected(s)}>
                <td><span className={`mh-make-pill ${HEALTH[s.health]?.cls || 'status-info'}`}>{HEALTH[s.health]?.label || s.health}</span></td>
                <td className="mh-site-cell">{s.name}{s.is_invalid && <span className="status-error mh-tag">invalide</span>}</td>
                <td className="mh-muted">{s.team_name}</td>
                <td>{cadence(s)}</td>
                <td className="mh-muted">
                  {s.last_execution_at
                    ? <>{s.last_execution_status === 1 ? '🟢' : '🔴'} {fmtDateTime(s.last_execution_at)}</>
                    : (s.is_active ? <span className="status-warning">jamais</span> : '—')}
                </td>
                <td>
                  {anomalies.length === 0 ? <span className="mh-muted">—</span> : (
                    <div className="mh-anoms">
                      {anomalies.map(code => {
                        const m = ANOMALY[code]; if (!m) return null;
                        return <span key={code} className={`mh-anom ${m.cls}`} title={m.label}>{m.label}</span>;
                      })}
                    </div>
                  )}
                </td>
                <td className="mh-muted">{fmtDate(s.last_edit)}{s.updated_by ? ` · ${s.updated_by}` : ''}</td>
                <td onClick={e => e.stopPropagation()}>
                  <a className="mh-link" href={`https://${config?.zone}/${s.team_id}/scenarios/${s.scenario_id}/edit`} target="_blank" rel="noreferrer">Make ↗</a>
                </td>
              </tr>
            );})}
            {filtered.length === 0 && <tr><td colSpan={8} className="mh-empty">{scenarios.length === 0 ? 'Aucune donnée — lance une synchronisation.' : (scope === 'anomalies' ? '✅ Aucune anomalie détectée.' : 'Aucun scénario pour ces filtres.')}</td></tr>}
          </tbody>
        </table>
      </div>

      {selected && <ScenarioDrawer scenario={selected} zone={config?.zone} onClose={() => setSelected(null)} />}
    </div>
  );
}

function ScenarioDrawer({ scenario, zone, onClose }) {
  const [exec, setExec] = useState(null);
  useEffect(() => {
    mhApi.makeExecutions(scenario.scenario_id).then(r => setExec(r.data)).catch(() => setExec({ executions: [] }));
  }, [scenario.scenario_id]);
  const apps = Array.isArray(scenario.used_apps) ? scenario.used_apps : [];

  return (
    <div className="mh-drawer-overlay" onClick={onClose}>
      <div className="mh-drawer" onClick={e => e.stopPropagation()}>
        <div className="mh-drawer-head">
          <h2>{scenario.name}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="mh-drawer-body">
          <span className={`mh-make-pill ${HEALTH[scenario.health]?.cls || 'status-info'}`}>{HEALTH[scenario.health]?.label}</span>
          <a className="mh-link" style={{ marginLeft: 10 }} href={`https://${zone}/${scenario.team_id}/scenarios/${scenario.scenario_id}/edit`} target="_blank" rel="noreferrer">Ouvrir dans Make ↗</a>
          {(scenario.anomalies || []).length > 0 && (
            <div className="mh-anoms" style={{ marginTop: 10 }}>
              {(scenario.anomalies || []).map(code => {
                const m = ANOMALY[code]; if (!m) return null;
                return <span key={code} className={`mh-anom ${m.cls}`}>{m.label}</span>;
              })}
            </div>
          )}
          <div className="mh-kv">
            <div><span>Team</span>{scenario.team_name}</div>
            <div><span>Cadence</span>{cadence(scenario)}</div>
            <div><span>Actif</span>{scenario.is_active ? 'Oui' : 'Non'}</div>
            <div><span>Dernière exéc</span>{scenario.last_execution_at ? `${scenario.last_execution_status === 1 ? '🟢' : '🔴'} ${fmtDateTime(scenario.last_execution_at)}` : (scenario.is_active ? 'jamais' : '—')}</div>
            <div><span>Prochaine exéc</span>{scenario.next_exec ? fmtDateTime(scenario.next_exec) : '—'}</div>
            <div><span>Opérations (30 j)</span>{fmtNum(scenario.operations)}</div>
            <div><span>Transfert</span>{fmtBytes(scenario.transfer)}</div>
            <div><span>Erreurs</span>{scenario.errors}</div>
            <div><span>Incomplètes (DLQ)</span>{scenario.dlq_count}</div>
            <div><span>Dernière modif</span>{fmtDate(scenario.last_edit)} {scenario.updated_by ? `· ${scenario.updated_by}` : ''}</div>
          </div>

          <h3>Apps / connexions</h3>
          <div className="mh-make-apps">{apps.length ? apps.map(a => <span key={a} className="mh-make-app">{a}</span>) : <span className="mh-muted">—</span>}</div>

          <h3>Dernières exécutions</h3>
          {!exec ? <p className="mh-muted">Chargement…</p> : exec.executions.length === 0 ? <p className="mh-muted">Aucune exécution récente.</p> : (
            <table className="mh-mini-table">
              <tbody>
                {exec.executions.map(e => (
                  <tr key={e.id}>
                    <td>{e.ok ? '🟢' : '🔴'}</td>
                    <td>{fmtDateTime(e.timestamp)}</td>
                    <td className="mh-muted">{e.type}</td>
                    <td>{(e.duration_ms / 1000).toFixed(1)} s</td>
                    <td className="mh-right">{e.operations} op.</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
